import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { TASK_NAVIGATION_SECTION } from "@nexus/contracts";
import {
  Button,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Toggle,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ClearFiltersButton } from "@/components/ClearFiltersButton";
import { FiltersToggleButton } from "@/components/FiltersToggleButton";
import type {
  MarketplaceOrder,
  SentScriptsMap,
} from "@/features/marketplace/types/marketplace.types";
import { formatOpportunityEngineLabel } from "@/features/opportunities/lib/format-opportunity";
import { triggerBlobDownload } from "@/features/opportunities/lib/triggerBlobDownload.js";
import {
  hubDocumentGroupRowsQueryOptions,
  hubOpportunityFacetsQueryOptions,
  industriesQueryOptions,
  useTogglePersonaBriefFavorite,
  useTogglePersonaScriptFavorite,
} from "@/lib/queries";

import type { PersonaOpportunityStatusFilter } from "../../../types/persona-opportunities.types.js";
import { PERSONA_OPPORTUNITY_SET_GRID_CLASS } from "../../../lib/persona-opportunity-grid-classes.js";
import { PERSONA_OPPORTUNITIES_LAYOUT_ID } from "../../../lib/persona-opportunity-stage-chrome.js";
import {
  personaSetCanOfferRegenerate,
  personaSetCanOfferRetry,
} from "../../../lib/persona-opportunity-set-mapper.js";
import { PersonaOpportunitiesEmptyState } from "../PersonaOpportunitiesEmptyState/PersonaOpportunitiesEmptyState.js";
import { PersonaOpportunitySetCard } from "../../sets/PersonaOpportunitySetCard/PersonaOpportunitySetCard.js";

import {
  DEFAULT_OPPORTUNITY_HUB_FILTERS,
  clearHubFilters,
  countActiveHubFilters,
  expandedHubOpportunityGroupKeys,
  filterHubOpportunityRows,
  filterHubScriptGroups,
  hubShowsEmptyDocumentsFooter,
  hubShowsEmptySetFooter,
  applyHubDatasetChange,
  type OpportunityFilterPresence,
  type OpportunityHubFilters,
} from "./hub-chrome-filters.js";
import {
  groupOpportunityRows,
  isBareNoPersonaList,
  isNoPersonaGroup,
  isPostFlightOnlyHubView,
  placeNoPersonaGroupLast,
  selectionMixFromSets,
} from "./hub-chrome-grouping.js";
import {
  buildLegacyTableColumns,
  buildPersonaTableColumns,
  buildPostFlightTableColumns,
} from "./hub-opportunity-columns.js";
import type { HubOpportunityServerQuery, HubOpportunityWindow } from "./hub-opportunity-window.js";
import { groupScriptRowsByOpportunity } from "./hub-script-table.js";
import { useHubVirtualList } from "./useHubVirtualList.js";
import {
  DEFAULT_HUB_OPPORTUNITY_TABLE_STATE,
  buildOpportunityCsv,
  displayedHubSort,
  isHubOpportunityServerSortableColumn,
  nextSort,
  orderColumns,
  sortOpportunityRows,
  visibleColumns,
  type HubOpportunityTableState,
} from "./hub-opportunity-table.js";
import {
  EMPTY_OPPORTUNITY_HUB_LIST_STATE,
  OpportunityHubListFooter,
  type OpportunityHubListState,
} from "./OpportunityHubListFooter.js";
import {
  hubTabPill,
  scopeOpportunityRows,
  scopeScriptRows,
  type OpportunityHubSelection,
  type OpportunityHubSelectionLevel,
  type OpportunityHubTabPill,
} from "./hub-chrome-selection.js";
import {
  DEFAULT_OPPORTUNITY_HUB_VIEWS,
  OPPORTUNITY_HUB_TAB_KEYS,
  type HubChromeOpportunityRow,
  type HubChromeOrderRow,
  type HubChromeScriptRow,
  type HubChromeSet,
  type HubScriptWindow,
  type HubSetsById,
  type OpportunityHubSelectionMix,
  type OpportunityHubSetStatusFilter,
  type OpportunityHubSetTypeFilter,
  type OpportunityHubTabCounts,
  type OpportunityHubTabKey,
  type OpportunityHubViewMode,
  type OpportunityHubViews,
} from "./hub-chrome.types.js";
import { hubSetTypeLabel } from "./hub-set-type-label.js";
import { OpportunityHubColumnsPicker } from "./OpportunityHubColumnsPicker.js";
import { OpportunityHubFoldToggle } from "./OpportunityHubFoldToggle.js";
import {
  OpportunityHubOpportunitiesCards,
  OpportunityHubScriptsPanel,
} from "./OpportunityHubPanels.js";
import { OpportunityHubProductionPanel } from "./OpportunityHubProductionPanel.js";
import { OpportunityHubScopePill } from "./OpportunityHubScopePill.js";
import { OpportunityHubSelectionPill } from "./OpportunityHubSelectionPill.js";
import { OpportunityHubOpportunityDrawer } from "./OpportunityHubOpportunityDrawer.js";
import { OpportunityHubScriptDrawer } from "./OpportunityHubScriptDrawer.js";
import { OpportunityHubOpportunitiesTable } from "./OpportunityHubOpportunitiesTable.js";
import { OpportunityHubSetsTable } from "./OpportunityHubSetsTable.js";
import { OpportunityHubTableMenu } from "./OpportunityHubTableMenu.js";
import { OpportunityHubViewToggle } from "./OpportunityHubViewToggle.js";

const TAB_I18N: Record<OpportunityHubTabKey, { key: string; fallback: string }> = {
  sets: { key: "personaOpportunities.tabs.sets", fallback: "Sets" },
  opportunities: { key: "personaOpportunities.tabs.opportunities", fallback: "Opportunities" },
  scripts: { key: "personaOpportunities.tabs.scriptsAndBriefs", fallback: "Scripts & Briefs" },
  sent_to_vendor: { key: "personaOpportunities.tabs.sentToVendor", fallback: "Production" },
};

export interface PersonaOpportunityHubChromeProps {
  sets: HubChromeSet[];
  /** Rows already scoped to the selected sets by the server; empty selection means every set. */
  opportunities: HubChromeOpportunityRow[];
  /** Rows of every loaded script group — the drawer and selection read from these. */
  scripts: HubChromeScriptRow[];
  /** Server-grouped Scripts & Briefs. When omitted, `scripts` are grouped here, all expanded. */
  scriptWindow?: HubScriptWindow;
  orders: HubChromeOrderRow[];
  /** Full marketplace orders for the Production tab. Flattened `orders` stay for older callers. */
  marketplaceOrders?: readonly MarketplaceOrder[];
  /** Paging state per list-bearing tab. Sets arrive in one list request. */
  listStates?: Partial<Record<OpportunityHubTabKey, OpportunityHubListState>>;
  selection: OpportunityHubSelection;
  activeTab: OpportunityHubTabKey;
  /** Exact totals for every tab. Null while the counts request is in flight. */
  tabCounts?: OpportunityHubTabCounts | null;
  views?: OpportunityHubViews;
  canCreateSet?: boolean;
  /** Header Create (not Generate) for a name-only Custom set (AD-3328). */
  canCreateEmptyCustomSetOnly?: boolean;
  isCreatingSet?: boolean;
  onCreateSet?: () => void;
  onCreateScript?: () => void;
  /** Set lifecycle, unchanged from the legacy grid. Omit to hide the action. */
  onStopSet?: (set: HubChromeSet) => void;
  onDeleteSet?: (set: HubChromeSet) => void;
  onRetrySet?: (set: HubChromeSet) => void;
  onRenameSet?: (set: HubChromeSet) => void;
  onRegenerateSet?: (set: HubChromeSet) => void;
  onTabChange: (tab: OpportunityHubTabKey) => void;
  tenantId?: string | null;
  sentScripts?: SentScriptsMap;
  onToggleSelect: (level: OpportunityHubSelectionLevel, id: string) => void;
  /** Set card body: exclusive select and leave the Sets tab. */
  onOpenSet?: (setId: string) => void;
  /** Bulk select or clear, used by folded groups and header checkboxes. */
  onToggleMembers: (level: OpportunityHubSelectionLevel, ids: string[]) => void;
  onClearLevel: (level: OpportunityHubSelectionLevel) => void;
  onViewChange: (tab: OpportunityHubTabKey, view: OpportunityHubViewMode) => void;
  /** Server group index. When set, opportunity rows are not filtered again on the client. */
  opportunityWindow?: HubOpportunityWindow;
  onOpportunityQueryChange?: (query: HubOpportunityServerQuery) => void;
  /** Increments when a set card opens, so persona groups expand once their index arrives. */
  expandGroupsRequest?: number;
  /** Increments when the opportunity scope resets, so the window asks for rows again. */
  groupLoadGeneration?: number;
  onOpportunityGroupsNeeded?: (groupKeys: readonly string[]) => void;
  onLoadMoreOpportunityGroups?: () => void;
  onLoadMoreOpportunityGroup?: (groupKey: string) => void;
  /** Shareable set scope. Empty means every set. */
  scopedSetIds?: readonly string[];
  /** Legacy facet values from the hub URL. `dataset` omitted means not chosen yet. */
  legacyFacets?: {
    action: string;
    dataset?: string;
    feature: string;
    subFeature: string;
    engine: string;
  };
  onLegacyFacetsChange?: (next: {
    action: string;
    dataset: string;
    flowName: string;
    feature: string;
    subFeature: string;
    engine: string;
  }) => void;
  onExportOpportunities?: () => Promise<HubChromeOpportunityRow[]>;
  /** Cards view: open generate scripts for one persona opportunity. */
  onGenerateOpportunityScripts?: (row: HubChromeOpportunityRow) => void;
  /** Ensures a Post-Flight opportunity is in drilldown before opening Scripts from its drawer. */
  onEnsureScriptOpportunity?: (opportunityId: string) => void;
}

const SCOPE_I18N: Record<
  OpportunityHubSelectionLevel,
  { key: string; one: string; other: string }
> = {
  sets: {
    key: "personaOpportunities.hub.selection.forSets",
    one: "for {{count}} set",
    other: "for {{count}} sets",
  },
  opportunities: {
    key: "personaOpportunities.hub.selection.forOpportunities",
    one: "for {{count}} opportunity",
    other: "for {{count}} opportunities",
  },
  scripts: {
    key: "personaOpportunities.hub.selection.forScripts",
    one: "for {{count}} script",
    other: "for {{count}} scripts",
  },
};

const CLEAR_ARIA_I18N: Record<OpportunityHubSelectionLevel, { key: string; fallback: string }> = {
  sets: { key: "personaOpportunities.hub.selection.clearSetsAria", fallback: "Clear set selection" },
  opportunities: {
    key: "personaOpportunities.hub.selection.clearOpportunitiesAria",
    fallback: "Clear opportunity selection",
  },
  scripts: {
    key: "personaOpportunities.hub.selection.clearScriptsAria",
    fallback: "Clear script selection",
  },
};

function opportunityRowName(row: HubChromeOpportunityRow): string {
  return row.setType === "COMPETITOR" || row.setType === "POST_FLIGHT" ? (row.topicTitle ?? row.angle) : row.angle;
}

function tabPillLabel(
  t: ReturnType<typeof useTranslation>["t"],
  pill: OpportunityHubTabPill,
  selectedSetNames: readonly string[],
) {
  if (pill.isOwnSelection) {
    if (pill.level === "sets" && pill.count === 1 && selectedSetNames[0]) {
      return selectedSetNames[0];
    }
    return t("personaOpportunities.hub.selection.selected", {
      count: pill.count,
      defaultValue_one: "{{count}} selected",
      defaultValue_other: "{{count}} selected",
    });
  }
  const scope = SCOPE_I18N[pill.level];
  return t(scope.key, {
    count: pill.count,
    defaultValue_one: scope.one,
    defaultValue_other: scope.other,
  });
}

export function PersonaOpportunityHubChrome({
  sets,
  opportunities,
  scripts,
  scriptWindow,
  marketplaceOrders = [],
  listStates,
  selection,
  activeTab,
  tabCounts = null,
  views = DEFAULT_OPPORTUNITY_HUB_VIEWS,
  canCreateSet = true,
  canCreateEmptyCustomSetOnly = false,
  isCreatingSet,
  onCreateSet,
  onCreateScript,
  onStopSet,
  onDeleteSet,
  onRetrySet,
  onRenameSet,
  onRegenerateSet,
  onTabChange,
  tenantId = null,
  sentScripts = {},
  onToggleSelect,
  onOpenSet,
  onToggleMembers,
  onClearLevel,
  onViewChange,
  opportunityWindow,
  onOpportunityQueryChange,
  expandGroupsRequest = 0,
  groupLoadGeneration = 0,
  onOpportunityGroupsNeeded,
  onLoadMoreOpportunityGroups,
  onLoadMoreOpportunityGroup,
  onExportOpportunities,
  onGenerateOpportunityScripts,
  onEnsureScriptOpportunity,
  scopedSetIds = [],
  legacyFacets,
  onLegacyFacetsChange,
}: PersonaOpportunityHubChromeProps) {
  const { t } = useTranslation();
  const toggleScriptFavorite = useTogglePersonaScriptFavorite();
  const toggleBriefFavorite = useTogglePersonaBriefFavorite();
  const [filters, setFilters] = React.useState<OpportunityHubFilters>(() => ({
    ...DEFAULT_OPPORTUNITY_HUB_FILTERS,
    ...(legacyFacets
      ? {
          opportunityAction: legacyFacets.action || "all",
          opportunityFlow: legacyFacets.dataset ?? "all",
          opportunityFeature: legacyFacets.feature || "all",
          opportunitySubFeature: legacyFacets.subFeature || "all",
          opportunityEngine: legacyFacets.engine || "all",
        }
      : {}),
  }));
  const [tableState, setTableState] = React.useState<HubOpportunityTableState>(
    DEFAULT_HUB_OPPORTUNITY_TABLE_STATE,
  );
  const [viewedOpportunityId, setViewedOpportunityId] = React.useState<string | null>(null);
  const viewedOpportunity =
    viewedOpportunityId == null
      ? null
      : (opportunities.find((row) => row.id === viewedOpportunityId) ?? null);
  const [viewedScriptId, setViewedScriptId] = React.useState<string | null>(null);
  const viewedScript =
    viewedScriptId == null ? null : (scripts.find((row) => row.id === viewedScriptId) ?? null);
  const pendingScriptIdRef = React.useRef<string | null>(null);
  const postFlightOpportunityId =
    viewedOpportunity?.setType === "POST_FLIGHT" ? viewedOpportunity.id : null;
  const postFlightDocuments = useQuery({
    ...hubDocumentGroupRowsQueryOptions(tenantId, postFlightOpportunityId ?? ""),
    enabled: Boolean(tenantId) && postFlightOpportunityId != null,
  });
  const postFlightDrawerScripts = React.useMemo(
    () =>
      (postFlightDocuments.data ?? [])
        .filter((document) => document.kind === "SCRIPT")
        .map((document) => ({
          id: document.id,
          title:
            document.title?.trim() ||
            t("personaOpportunities.hub.scripts.untitledScript", "Untitled script"),
        })),
    [postFlightDocuments.data, t],
  );

  React.useEffect(() => {
    if (viewedOpportunityId != null && viewedOpportunity == null) {
      setViewedOpportunityId(null);
    }
  }, [viewedOpportunity, viewedOpportunityId]);

  React.useEffect(() => {
    if (viewedScriptId != null && viewedScript == null) {
      setViewedScriptId(null);
    }
  }, [viewedScript, viewedScriptId]);

  React.useEffect(() => {
    const pending = pendingScriptIdRef.current;
    if (!pending || !scripts.some((row) => row.id === pending)) return;
    pendingScriptIdRef.current = null;
    setViewedOpportunityId(null);
    setViewedScriptId(pending);
  }, [scripts]);

  const setFilter = <K extends keyof OpportunityHubFilters>(
    key: K,
    value: OpportunityHubFilters[K],
  ) => {
    setFilters((current) => ({ ...current, [key]: value }));
    if (
      key !== "opportunityAction" &&
      key !== "opportunityFeature" &&
      key !== "opportunitySubFeature" &&
      key !== "opportunityEngine"
    ) {
      return;
    }
    const next = { ...filters, [key]: value };
    onLegacyFacetsChange?.({
      action: next.opportunityAction,
      dataset: next.opportunityFlow,
      flowName: next.opportunityFlow,
      feature: next.opportunityFeature,
      subFeature: next.opportunitySubFeature,
      engine: next.opportunityEngine,
    });
  };

  const patchTableState = (patch: Partial<HubOpportunityTableState>) => {
    setTableState((current) => ({ ...current, ...patch }));
  };

  const selectedSetIds = React.useMemo(() => new Set(selection.sets), [selection.sets]);
  const selectedOpportunityIds = React.useMemo(
    () => new Set(selection.opportunities),
    [selection.opportunities],
  );
  const selectedScriptIds = React.useMemo(() => new Set(selection.scripts), [selection.scripts]);
  const setsById = React.useMemo<HubSetsById>(
    () => new Map(sets.map((set) => [set.id, set])),
    [sets],
  );
  const scopedSets = selection.sets.length > 0 ? sets.filter((set) => selectedSetIds.has(set.id)) : sets;
  /** Nothing selected means every set is in view, so that counts as several too. */
  const hasSeveralSetsInView = selection.sets.length > 1 || scopedSets.length > 1;
  const mix = selectionMixFromSets(scopedSets);
  const view = views[activeTab];
  const selectedSets = React.useMemo(
    () =>
      selection.sets.flatMap((id) => {
        const set = setsById.get(id);
        return set ? [set] : [];
      }),
    [selection.sets, setsById],
  );
  const selectedSetNames = React.useMemo(
    () => selectedSets.map((set) => set.name),
    [selectedSets],
  );
  const selectedOpportunityNames = React.useMemo(() => {
    const labels = new Map(opportunities.map((row) => [row.id, opportunityRowName(row)]));
    return selection.opportunities.flatMap((id) => {
      const name = labels.get(id);
      return name ? [name] : [];
    });
  }, [opportunities, selection.opportunities]);
  const selectedScriptNames = React.useMemo(() => {
    const titles = new Map(scripts.map((row) => [row.id, row.title]));
    return selection.scripts.flatMap((id) => {
      const title = titles.get(id);
      return title ? [title] : [];
    });
  }, [scripts, selection.scripts]);
  const selectedNamesByLevel: Record<OpportunityHubSelectionLevel, readonly string[]> = {
    sets: selectedSetNames,
    opportunities: selectedOpportunityNames,
    scripts: selectedScriptNames,
  };

  const visibleSets = React.useMemo(() => {
    const query = filters.setsSearch.trim().toLowerCase();
    return sets.filter((set) => {
      if (filters.setsStatus !== "all" && set.status !== filters.setsStatus) return false;
      if (filters.setsType !== "all" && set.type !== filters.setsType) return false;
      if (query && !set.name.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [sets, filters.setsSearch, filters.setsStatus, filters.setsType]);

  const scopedOpportunities = React.useMemo(
    () =>
      opportunityWindow
        ? scopeOpportunityRows(opportunities, selection)
        : filterHubOpportunityRows(scopeOpportunityRows(opportunities, selection), filters),
    [opportunityWindow, opportunities, selection, filters],
  );

  const scriptSourceGroups =
    scriptWindow?.groups ?? groupScriptRowsByOpportunity(scopeScriptRows(scripts, selection));
  const scriptGroups = React.useMemo(
    () => filterHubScriptGroups(scriptSourceGroups, filters),
    [scriptSourceGroups, filters],
  );
  const [collapsedLocalScriptGroups, setCollapsedLocalScriptGroups] = React.useState<
    ReadonlySet<string>
  >(() => new Set());
  const expandedScriptGroupKeys = React.useMemo(
    () =>
      scriptWindow?.expandedKeys ??
      new Set(
        scriptGroups
          .map((group) => group.key)
          .filter((key) => !collapsedLocalScriptGroups.has(key)),
      ),
    [scriptWindow, scriptGroups, collapsedLocalScriptGroups],
  );
  const toggleScriptGroup = (groupKey: string) => {
    if (scriptWindow) {
      scriptWindow.onToggleGroup(groupKey);
      return;
    }
    setCollapsedLocalScriptGroups((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };
  const visibleScripts = React.useMemo(
    () =>
      scriptGroups.flatMap((group) => (expandedScriptGroupKeys.has(group.key) ? group.rows : [])),
    [scriptGroups, expandedScriptGroupKeys],
  );

  const { data: industries } = useQuery(industriesQueryOptions("persona-opportunities", tenantId));
  const industryLabelByIndex = React.useMemo(() => {
    const labels = new Map<string, string>();
    for (const industry of industries ?? []) labels.set(industry.index, industry.title);
    return labels;
  }, [industries]);
  const personaColumns = React.useMemo(
    () => orderColumns(buildPersonaTableColumns(t), tableState.columnOrder),
    [t, tableState.columnOrder],
  );
  const legacyColumns = React.useMemo(
    () => orderColumns(buildLegacyTableColumns(t, industryLabelByIndex), tableState.columnOrder),
    [t, tableState.columnOrder, industryLabelByIndex],
  );
  const shownPersonaColumns = React.useMemo(
    () => visibleColumns(personaColumns, tableState.hiddenColumnIds),
    [personaColumns, tableState.hiddenColumnIds],
  );
  const shownLegacyColumns = React.useMemo(
    () => visibleColumns(legacyColumns, tableState.hiddenColumnIds),
    [legacyColumns, tableState.hiddenColumnIds],
  );
  const postFlightColumns = React.useMemo(
    () => orderColumns(buildPostFlightTableColumns(t), tableState.columnOrder),
    [t, tableState.columnOrder],
  );
  const shownPostFlightColumns = React.useMemo(
    () => visibleColumns(postFlightColumns, tableState.hiddenColumnIds),
    [postFlightColumns, tableState.hiddenColumnIds],
  );
  const setsInScope = scopedSets.length > 0 ? scopedSets : sets;
  const postFlightOnlyView = isPostFlightOnlyHubView(filters.opportunityType, setsInScope);
  const hasCompetitorSetsInScope = setsInScope.some((set) => set.type === "COMPETITOR");
  const clientSortColumns = postFlightOnlyView
    ? postFlightColumns
    : [...personaColumns, ...legacyColumns];
  const clientOpportunityGroups = React.useMemo(
    () =>
      groupOpportunityRows(
        sortOpportunityRows(
          scopedOpportunities,
          displayedHubSort(
            tableState.sort,
            hasCompetitorSetsInScope && !postFlightOnlyView,
          ),
          clientSortColumns,
        ),
        "persona",
      ),
    [
      scopedOpportunities,
      tableState.sort,
      clientSortColumns,
      hasCompetitorSetsInScope,
      postFlightOnlyView,
    ],
  );
  const opportunityGroups = React.useMemo(() => {
    const groups = placeNoPersonaGroupLast(opportunityWindow?.groups ?? clientOpportunityGroups);
    if (!hasSeveralSetsInView || filters.opportunitySet === "all") return groups;
    if (!groups.some((group) => group.setId)) return groups;
    return groups.filter((group) => group.setId === filters.opportunitySet);
  }, [
    opportunityWindow?.groups,
    clientOpportunityGroups,
    hasSeveralSetsInView,
    filters.opportunitySet,
  ]);
  const expandedGroupKeys = React.useMemo(
    () =>
      expandedHubOpportunityGroupKeys(
        filters.opportunityStatus,
        opportunityGroups.map((group) => group.key),
        tableState.expandedGroupKeys,
      ),
    [filters.opportunityStatus, opportunityGroups, tableState.expandedGroupKeys],
  );
  const appliedExpandRequestRef = React.useRef(0);
  React.useEffect(() => {
    if (expandGroupsRequest === 0 || expandGroupsRequest === appliedExpandRequestRef.current) {
      return;
    }
    if (opportunityGroups.length === 0) return;
    appliedExpandRequestRef.current = expandGroupsRequest;
    setTableState((current) => ({
      ...current,
      expandedGroupKeys: opportunityGroups.map((group) => group.key),
    }));
  }, [expandGroupsRequest, opportunityGroups]);
  const bareNoPersona = isBareNoPersonaList(opportunityGroups);
  const sawNoPersonaGroup = React.useRef(false);
  if (opportunityGroups.some((group) => isNoPersonaGroup(group))) {
    sawNoPersonaGroup.current = true;
  }
  const opportunityPresence = {
    hasPersonaGroups: opportunityGroups.some((group) => !isNoPersonaGroup(group)),
    hasNoPersonaGroup:
      sawNoPersonaGroup.current || opportunityGroups.some((group) => isNoPersonaGroup(group)),
    setsInViewCount: hasSeveralSetsInView ? Math.max(scopedSets.length, 2) : 1,
  };
  const facetDataset =
    filters.opportunityFlow === "all" ? "all" : filters.opportunityFlow;
  const facetsQuery = useQuery({
    ...hubOpportunityFacetsQueryOptions(tenantId, scopedSetIds, facetDataset, {
      enabled: activeTab === "opportunities" && opportunityPresence.hasNoPersonaGroup,
    }),
  });
  const legacyFacetsData = facetsQuery.data ?? {
    actions: [],
    datasets: [],
    features: [],
    subFeatures: [],
    engines: [],
  };
  const publishLegacyFacets = (next: OpportunityHubFilters) => {
    onLegacyFacetsChange?.({
      action: next.opportunityAction,
      dataset: next.opportunityFlow,
      flowName: next.opportunityFlow,
      feature: next.opportunityFeature,
      subFeature: next.opportunitySubFeature,
      engine: next.opportunityEngine,
    });
  };
  React.useEffect(() => {
    if (!legacyFacets) return;
    setFilters((current) => {
      const opportunityFlow = legacyFacets.dataset ?? current.opportunityFlow;
      const next = {
        ...current,
        opportunityAction: legacyFacets.action || "all",
        opportunityFlow,
        opportunityFeature: legacyFacets.feature || "all",
        opportunitySubFeature: legacyFacets.subFeature || "all",
        opportunityEngine: legacyFacets.engine || "all",
      };
      if (
        next.opportunityAction === current.opportunityAction &&
        next.opportunityFlow === current.opportunityFlow &&
        next.opportunityFeature === current.opportunityFeature &&
        next.opportunitySubFeature === current.opportunitySubFeature &&
        next.opportunityEngine === current.opportunityEngine
      ) {
        return current;
      }
      return next;
    });
  }, [
    legacyFacets?.action,
    legacyFacets?.dataset,
    legacyFacets?.feature,
    legacyFacets?.subFeature,
    legacyFacets?.engine,
  ]);
  const foldableKeys = bareNoPersona ? [] : opportunityGroups.map((group) => group.key);
  const pickerColumns = postFlightOnlyView
    ? postFlightColumns
    : [
        ...(opportunityPresence.hasPersonaGroups ? personaColumns : []),
        ...(opportunityPresence.hasNoPersonaGroup ? legacyColumns : []),
      ];

  const toggleGroupExpanded = (key: string) => {
    setTableState((current) => ({
      ...current,
      expandedGroupKeys: current.expandedGroupKeys.includes(key)
        ? current.expandedGroupKeys.filter((expanded) => expanded !== key)
        : [...current.expandedGroupKeys, key],
    }));
  };

  const competitorColumnSort = opportunityPresence.hasNoPersonaGroup && !postFlightOnlyView;
  const displayedSort = displayedHubSort(tableState.sort, competitorColumnSort);

  const changeSort = (columnId: string) => {
    setTableState((current) => {
      const base = displayedHubSort(current.sort, competitorColumnSort);
      const next = nextSort(base, columnId);
      if (next == null && competitorColumnSort && columnId === "findings") {
        return { ...current, sort: { columnId: "findings", direction: "asc" } };
      }
      return { ...current, sort: next };
    });
  };

  const listFooter = (tab: OpportunityHubTabKey, isEmpty: boolean, emptyLabel: string) => (
    <OpportunityHubListFooter
      state={listStates?.[tab] ?? EMPTY_OPPORTUNITY_HUB_LIST_STATE}
      isEmpty={isEmpty}
      emptyLabel={emptyLabel}
    />
  );

  const hasExplicitSetSelection = selection.sets.length > 0;
  const opportunitiesFooter = listFooter(
    "opportunities",
    opportunityWindow
      ? !opportunityWindow.isLoading && opportunityWindow.groups.length === 0
      : scopedOpportunities.length === 0,
    hubShowsEmptySetFooter(hasExplicitSetSelection, scopedSets)
      ? t(
          "personaOpportunities.hub.opportunities.emptySet",
          "No opportunities in this set yet.",
        )
      : t("personaOpportunities.hub.opportunities.empty", "No opportunities match these filters."),
  );
  const scriptsFooter = listFooter(
    "scripts",
    scriptGroups.length === 0,
    hubShowsEmptyDocumentsFooter(hasExplicitSetSelection, scopedSets)
      ? t(
          "personaOpportunities.hub.scripts.emptySet",
          "No scripts yet. Add a script to get started.",
        )
      : t("personaOpportunities.hub.scripts.empty", "No scripts or briefs match these filters."),
  );
  const [isExportingOpportunities, setIsExportingOpportunities] = React.useState(false);
  const exportOpportunitiesCsv = () => {
    if (isExportingOpportunities) return;
    const download = (rows: HubChromeOpportunityRow[]) => {
      const csv = buildOpportunityCsv(
        rows,
        [
          ...(postFlightOnlyView
            ? shownPostFlightColumns
            : [
                ...(opportunityPresence.hasPersonaGroups ? shownPersonaColumns : []),
                ...(opportunityPresence.hasNoPersonaGroup ? shownLegacyColumns : []),
              ]),
        ],
        t("personaOpportunities.hub.opportunities.columns.driving", "Driving"),
      );
      triggerBlobDownload(new Blob([csv], { type: "text/csv;charset=utf-8" }), "opportunities.csv");
    };
    if (!onExportOpportunities) {
      download(scopedOpportunities);
      return;
    }
    setIsExportingOpportunities(true);
    void onExportOpportunities()
      .then(download)
      .catch(() => {
        toast.error(
          t(
            "personaOpportunities.hub.opportunities.exportFailed",
            "Could not export opportunities",
          ),
        );
      })
      .finally(() => setIsExportingOpportunities(false));
  };

  const debouncedOpportunitySearch = useDebouncedValue(
    filters.opportunitiesSearch,
    SEARCH_DEBOUNCE_MS,
  );
  const setsScrollRef = React.useRef<HTMLDivElement>(null);
  const setColumns = useSetGridColumns();
  const setRowCount = Math.ceil(visibleSets.length / setColumns);
  const setsWindow = useHubVirtualList({
    count: setRowCount,
    // Card row plus the 16px gap-4 between rows.
    estimateSize: 256,
    scrollRef: setsScrollRef,
    hasMore: listStates?.sets?.hasMore,
    isFetchingMore: listStates?.sets?.isLoadingMore,
    onLoadMore: listStates?.sets?.onLoadMore,
  });

  const isFilteredSetInView = scopedSets.some((set) => set.id === filters.opportunitySet);
  React.useEffect(() => {
    if (filters.opportunitySet !== "all" && (!hasSeveralSetsInView || !isFilteredSetInView)) {
      setFilters((current) =>
        current.opportunitySet === "all" ? current : { ...current, opportunitySet: "all" },
      );
    }
  }, [filters.opportunitySet, hasSeveralSetsInView, isFilteredSetInView]);

  const isScriptSetInView = scopedSets.some((set) => set.id === filters.scriptSet);
  React.useEffect(() => {
    if (filters.scriptSet !== "all" && (!hasSeveralSetsInView || !isScriptSetInView)) {
      setFilters((current) =>
        current.scriptSet === "all" ? current : { ...current, scriptSet: "all" },
      );
    }
  }, [filters.scriptSet, hasSeveralSetsInView, isScriptSetInView]);

  React.useEffect(() => {
    if (!onOpportunityQueryChange) return;
    onOpportunityQueryChange({
      groupBy: "persona",
      filters: {
        search: debouncedOpportunitySearch.trim() || undefined,
        setId:
          hasSeveralSetsInView && filters.opportunitySet !== "all"
            ? filters.opportunitySet
            : undefined,
        status:
          opportunityPresence.hasPersonaGroups && filters.opportunityStatus !== "all"
            ? filters.opportunityStatus
            : undefined,
        action:
          opportunityPresence.hasNoPersonaGroup && filters.opportunityAction !== "all"
            ? filters.opportunityAction
            : undefined,
        dataset:
          opportunityPresence.hasNoPersonaGroup && filters.opportunityFlow !== "all"
            ? filters.opportunityFlow
            : undefined,
        feature:
          opportunityPresence.hasNoPersonaGroup && filters.opportunityFeature !== "all"
            ? filters.opportunityFeature
            : undefined,
        subFeature:
          opportunityPresence.hasNoPersonaGroup && filters.opportunitySubFeature !== "all"
            ? filters.opportunitySubFeature
            : undefined,
        engine:
          opportunityPresence.hasNoPersonaGroup && filters.opportunityEngine !== "all"
            ? filters.opportunityEngine
            : undefined,
        sort:
          displayedSort && isHubOpportunityServerSortableColumn(displayedSort.columnId)
            ? `${displayedSort.columnId}:${displayedSort.direction}`
            : undefined,
      },
    });
  }, [
    onOpportunityQueryChange,
    debouncedOpportunitySearch,
    filters.opportunitySet,
    selection.sets,
    filters.opportunityStatus,
    filters.opportunityAction,
    filters.opportunityFlow,
    filters.opportunityFeature,
    filters.opportunitySubFeature,
    filters.opportunityEngine,
    opportunityPresence.hasPersonaGroups,
    opportunityPresence.hasNoPersonaGroup,
    tableState.sort,
  ]);

  const toggleVisible = (level: OpportunityHubSelectionLevel, ids: string[]) => {
    onToggleMembers(level, ids);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <div
        id={PERSONA_OPPORTUNITIES_LAYOUT_ID}
        className="relative mx-auto flex min-h-[720px] w-full max-w-[1680px] flex-1 flex-col overflow-hidden px-8 pt-4 pb-3"
      >
        <div className="mb-3 flex shrink-0 flex-col-reverse gap-3 xl:flex-row xl:items-center xl:justify-between xl:gap-8">
          <Tabs
            className="min-w-0 gap-0 xl:shrink"
            value={activeTab}
            onValueChange={(value) => onTabChange(value as OpportunityHubTabKey)}
          >
            <TabsList
              variant="line"
              className="h-auto w-fit justify-start gap-1 border-transparent group-data-[orientation=horizontal]/tabs:h-auto"
            >
            {OPPORTUNITY_HUB_TAB_KEYS.map((key, index) => {
              const label = t(TAB_I18N[key].key, TAB_I18N[key].fallback);
              const count = tabCounts == null ? null : String(tabCounts[key]);
              const countedLabel = count != null ? `${label} ${count}` : label;
              const pill = hubTabPill(key, selection);
              const pillLabel = pill ? tabPillLabel(t, pill, selectedSetNames) : null;
              const pillSetNames =
                pill && !pill.isOwnSelection && pill.level === "sets" ? selectedSetNames : [];
              const triggerAria =
                pillLabel && pillSetNames.length > 1
                  ? t("personaOpportunities.hub.selection.tabForSetsAria", {
                      tab: countedLabel,
                      scope: pillLabel,
                      names: pillSetNames.join(", "),
                      defaultValue: "{{tab}} {{scope}}: {{names}}",
                    })
                  : pillLabel
                    ? `${countedLabel} ${pillLabel}`
                    : countedLabel;
              const trigger = (
                <TabsTrigger
                  value={key}
                  aria-label={triggerAria}
                  className="items-center gap-2 rounded-none px-3 pt-2 pb-3 text-sm data-[state=active]:font-semibold"
                >
                  {label}
                  {count != null ? (
                    <span className="text-xs font-normal tabular-nums text-muted-foreground">
                      {count}
                    </span>
                  ) : null}
                  {pill && !pill.isOwnSelection && pillLabel ? (
                    <OpportunityHubScopePill
                      level={pill.level}
                      label={pillLabel}
                      names={
                        pill.level === "sets" ? pillSetNames : selectedNamesByLevel[pill.level]
                      }
                      total={pill.count}
                    />
                  ) : null}
                </TabsTrigger>
              );

              const tab = pill?.isOwnSelection && pillLabel ? (
                <div className="flex items-stretch gap-1.5">
                  {trigger}
                  <span className="inline-flex items-center border border-transparent pt-2 pb-3">
                    <OpportunityHubSelectionPill
                      className="animate-in fade-in zoom-in-95 duration-150 ease-out motion-reduce:animate-none"
                      level={pill.level}
                      label={pillLabel}
                      sets={pill.level === "sets" ? selectedSets : []}
                      itemNames={pill.level === "sets" ? [] : selectedNamesByLevel[pill.level]}
                      itemCount={pill.count}
                      clearAriaLabel={t(
                        CLEAR_ARIA_I18N[pill.level].key,
                        CLEAR_ARIA_I18N[pill.level].fallback,
                      )}
                      onClear={() => onClearLevel(pill.level)}
                    />
                  </span>
                </div>
              ) : (
                trigger
              );

              return (
                <React.Fragment key={key}>
                  {index > 0 ? (
                    <span
                      aria-hidden
                      className="inline-flex select-none items-center pt-2 pb-3 text-sm text-muted-foreground"
                    >
                      &gt;
                    </span>
                  ) : null}
                  {tab}
                </React.Fragment>
              );
            })}
            </TabsList>
          </Tabs>
          {(canCreateSet || canCreateEmptyCustomSetOnly) && (onCreateSet || onCreateScript) ? (
            <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2">
              {onCreateSet ? (
                <Button
                  type="button"
                  size="default"
                  iconStart={isCreatingSet ? undefined : "plus"}
                  disabled={!!isCreatingSet}
                  onClick={onCreateSet}
                  className="border border-transparent"
                >
                  {isCreatingSet
                    ? t("common.creating")
                    : canCreateSet
                      ? t("personaOpportunities.hub.actions.generateSet", "Generate Set")
                      : t("personaOpportunities.hub.actions.createSet", "Create Set")}
                </Button>
              ) : null}
              {onCreateScript ? (
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  iconStart="sparkles"
                  onClick={onCreateScript}
                >
                  {t("personaOpportunities.hub.actions.createScript", "Custom Script")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                {activeTab === "sent_to_vendor" ? null : (
                <HubTabToolbar
                  tab={activeTab}
                  view={view}
                  onViewChange={(next) => onViewChange(activeTab, next)}
                  mix={mix}
                  opportunityPresence={opportunityPresence}
                  postFlightOnlyView={postFlightOnlyView}
                  legacyFacetOptions={legacyFacetsData}
                  filters={filters}
                  onFilterChange={setFilter}
                  onDatasetChange={(value) => {
                    const next = applyHubDatasetChange(filters, value);
                    setFilters(next);
                    publishLegacyFacets(next);
                  }}
                  onClearFilters={() => {
                    const next = clearHubFilters(activeTab, filters, mix, opportunityPresence);
                    setFilters(next);
                    publishLegacyFacets(next);
                  }}
                  setOptions={scopedSets.map((set) => ({ id: set.id, name: set.name }))}
                  tableControls={
                    activeTab === "opportunities" && view === "table" ? (
                      <OpportunityHubTableMenu
                        foldControl={
                          foldableKeys.length > 0 ? (
                            <OpportunityHubFoldToggle
                              isFolded={tableState.expandedGroupKeys.length === 0}
                              isUnfolded={foldableKeys.every((key) => expandedGroupKeys.has(key))}
                              onFoldAll={() => patchTableState({ expandedGroupKeys: [] })}
                              onUnfoldAll={() =>
                                patchTableState({ expandedGroupKeys: foldableKeys })
                              }
                            />
                          ) : undefined
                        }
                        columnsControl={
                          <OpportunityHubColumnsPicker
                            columns={pickerColumns}
                            hiddenColumnIds={tableState.hiddenColumnIds}
                            onHiddenColumnIdsChange={(ids) =>
                              patchTableState({ hiddenColumnIds: ids })
                            }
                            columnOrder={tableState.columnOrder}
                            onColumnOrderChange={(order) =>
                              patchTableState({ columnOrder: order })
                            }
                            onReset={() =>
                              patchTableState({ hiddenColumnIds: [], columnOrder: [] })
                            }
                          />
                        }
                        onExportCsv={exportOpportunitiesCsv}
                        exportDisabled={isExportingOpportunities}
                      />
                    ) : null
                  }
                />
                )}

                {sets.length === 0 && activeTab === "sets" ? (
                  <PersonaOpportunitiesEmptyState
                    canCreateSet={canCreateSet}
                    isCreatingSet={isCreatingSet}
                    onCreateSet={onCreateSet}
                  />
                ) : (
                  <>
                {activeTab === "sets" && view === "cards" ? (
                  <div
                    ref={setsScrollRef}
                    data-task-section={TASK_NAVIGATION_SECTION.PERSONA_OPPORTUNITY_SETS}
                    className="min-h-0 flex-1 scroll-mt-6 overflow-y-auto"
                  >
                    <div style={{ height: setsWindow.topPad }} />
                    <div className="flex flex-col gap-4">
                    {setsWindow.indexes.map((index) => {
                      const rowSets = visibleSets.slice(
                        index * setColumns,
                        index * setColumns + setColumns,
                      );
                      if (rowSets.length === 0) {
                        return listFooter(
                          "sets",
                          false,
                          "",
                        );
                      }
                      return (
                        <div key={rowSets[0]!.id} className={PERSONA_OPPORTUNITY_SET_GRID_CLASS}>
                          {rowSets.map((set) => (
                            <PersonaOpportunitySetCard
                              key={set.id}
                              set={set}
                              industryLabelByIndex={industryLabelByIndex}
                              sentCount={set.sentCount}
                              selected={selectedSetIds.has(set.id)}
                              onToggleSelect={() => onToggleSelect("sets", set.id)}
                              onOpen={onOpenSet ? () => onOpenSet(set.id) : undefined}
                              canMutate={canCreateSet}
                              onStop={onStopSet ? () => onStopSet(set) : undefined}
                              onDelete={onDeleteSet ? () => onDeleteSet(set) : undefined}
                              onRetry={
                                onRetrySet && personaSetCanOfferRetry(set)
                                  ? () => onRetrySet(set)
                                  : undefined
                              }
                              onRegenerate={
                                onRegenerateSet && personaSetCanOfferRegenerate(set)
                                  ? () => onRegenerateSet(set)
                                  : undefined
                              }
                              onRename={onRenameSet ? () => onRenameSet(set) : undefined}
                            />
                          ))}
                        </div>
                      );
                    })}
                    </div>
                    <div style={{ height: setsWindow.bottomPad }} />
                    {visibleSets.length === 0
                      ? listFooter(
                          "sets",
                          true,
                          t(
                            "personaOpportunities.hub.sets.empty",
                            "No sets match these filters.",
                          ),
                        )
                      : null}
                  </div>
                ) : null}

                {activeTab === "sets" && view === "table" ? (
                  <OpportunityHubSetsTable
                    sets={visibleSets}
                    selectedSetIds={selectedSetIds}
                    listState={listStates?.sets}
                    onToggleSet={(setId) => onToggleSelect("sets", setId)}
                    onToggleVisible={() =>
                      toggleVisible(
                        "sets",
                        visibleSets.map((set) => set.id),
                      )
                    }
                  />
                ) : null}

                {activeTab === "opportunities" && view === "table" ? (
                  <OpportunityHubOpportunitiesTable
                    groups={opportunityGroups}
                    personaColumns={shownPersonaColumns}
                    legacyColumns={shownLegacyColumns}
                    postFlightColumns={shownPostFlightColumns}
                    sort={displayedSort}
                    onSortChange={changeSort}
                    expandedGroupKeys={expandedGroupKeys}
                    onToggleGroupExpanded={toggleGroupExpanded}
                    selectedIds={selectedOpportunityIds}
                    onToggleSelect={(id) => onToggleSelect("opportunities", id)}
                    onToggleMembers={(ids) => onToggleMembers("opportunities", ids)}
                    onView={(row) => setViewedOpportunityId(row.id)}
                    footer={opportunitiesFooter}
                    loadingKeys={opportunityWindow?.loadingKeys}
                    hasMoreByKey={opportunityWindow?.hasMoreByKey}
                    isLoadingMoreGroups={opportunityWindow?.isLoadingMoreGroups}
                    hasMoreGroups={opportunityWindow?.hasMoreGroups}
                    onLoadMoreGroups={onLoadMoreOpportunityGroups}
                    onLoadMoreGroup={onLoadMoreOpportunityGroup}
                    onGroupsNeeded={onOpportunityGroupsNeeded}
                    groupLoadGeneration={groupLoadGeneration}
                    showSetBadge={hasSeveralSetsInView}
                    setsById={setsById}
                  />
                ) : null}
                {activeTab === "opportunities" && view === "cards" ? (
                  <OpportunityHubOpportunitiesCards
                    groups={opportunityGroups}
                    selectedIds={selectedOpportunityIds}
                    onToggleSelect={(id) => onToggleSelect("opportunities", id)}
                    onView={(row) => setViewedOpportunityId(row.id)}
                    onGenerateScripts={onGenerateOpportunityScripts}
                    footer={opportunitiesFooter}
                    expandedGroupKeys={expandedGroupKeys}
                    window={opportunityWindow}
                    onToggleGroupExpanded={toggleGroupExpanded}
                    onLoadMoreGroups={onLoadMoreOpportunityGroups}
                    onLoadMoreGroup={onLoadMoreOpportunityGroup}
                    onGroupsNeeded={onOpportunityGroupsNeeded}
                    groupLoadGeneration={groupLoadGeneration}
                    showSetBadge={hasSeveralSetsInView}
                    setsById={setsById}
                  />
                ) : null}
                {activeTab === "scripts" ? (
                  <OpportunityHubScriptsPanel
                    groups={scriptGroups}
                    setsById={setsById}
                    expandedKeys={expandedScriptGroupKeys}
                    onToggleGroup={toggleScriptGroup}
                    onPeekGroup={scriptWindow?.onPeekGroup}
                    view={view}
                    selectedIds={selectedScriptIds}
                    onToggleSelect={(id) => onToggleSelect("scripts", id)}
                    onToggleVisible={() =>
                      toggleVisible(
                        "scripts",
                        visibleScripts.map((row) => row.id),
                      )
                    }
                    onView={(row) => setViewedScriptId(row.id)}
                    onToggleFavorite={(row) => {
                      if (!tenantId) return;
                      const isFavorite = !row.isFavorite;
                      if (row.kind === "brief") {
                        toggleBriefFavorite.mutate({
                          setId: row.setId,
                          briefId: row.id,
                          tenantId,
                          isFavorite,
                        });
                        return;
                      }
                      toggleScriptFavorite.mutate({
                        setId: row.setId,
                        scriptId: row.id,
                        tenantId,
                        isFavorite,
                      });
                    }}
                    footer={scriptsFooter}
                    listState={listStates?.scripts}
                  />
                ) : null}
                {activeTab === "sent_to_vendor" ? (
                  <OpportunityHubProductionPanel
                    orders={marketplaceOrders}
                    selection={selection}
                    tenantId={tenantId}
                    listState={listStates?.sent_to_vendor}
                    onGoToOpportunities={() => onTabChange("opportunities")}
                  />
                ) : null}
                  </>
                )}
          </div>
      </div>
      <OpportunityHubOpportunityDrawer
        opportunity={viewedOpportunity}
        open={viewedOpportunity != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setViewedOpportunityId(null);
        }}
        scripts={postFlightDrawerScripts}
        onOpenScript={(scriptId) => {
          pendingScriptIdRef.current = scriptId;
          const opportunityId = viewedOpportunity?.id;
          if (opportunityId) {
            onEnsureScriptOpportunity?.(opportunityId);
            if (scriptWindow && !scriptWindow.expandedKeys.has(opportunityId)) {
              scriptWindow.onToggleGroup(opportunityId);
            }
          }
          onTabChange("scripts");
          if (scripts.some((row) => row.id === scriptId)) {
            pendingScriptIdRef.current = null;
            setViewedOpportunityId(null);
            setViewedScriptId(scriptId);
          }
        }}
      />
      {viewedScript ? (
        <OpportunityHubScriptDrawer
          row={viewedScript}
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setViewedScriptId(null);
          }}
          tenantId={tenantId}
          canEdit={canCreateSet}
          sentScripts={sentScripts}
        />
      ) : null}
    </div>
  );
}

interface HubFilterOption {
  value: string;
  label: string;
}

function facetOptions(
  values: readonly string[],
  selected: string,
  allLabel: string,
  labelFor: (value: string) => string,
): HubFilterOption[] {
  const list =
    selected !== "all" && !values.includes(selected) ? [...values, selected] : [...values];
  return [
    { value: "all", label: allLabel },
    ...list.map((value) => ({ value, label: labelFor(value) })),
  ];
}

function HubFilterSelect({
  value,
  onValueChange,
  ariaLabel,
  options,
}: {
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel: string;
  options: HubFilterOption[];
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-8 w-auto min-w-[7.5rem] max-w-[14rem]" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Narrows Scripts to starred rows; the star pops on press-on, matching the row star. */
function HubFavoritesToggle({
  isPressed,
  onPressedChange,
}: {
  isPressed: boolean;
  onPressedChange: (isPressed: boolean) => void;
}) {
  const { t } = useTranslation();
  // Bumped on each press-on so the pop replays; mounting already pressed never plays it.
  const [popKey, setPopKey] = React.useState(0);

  return (
    <Toggle
      variant="outline"
      size="sm"
      pressed={isPressed}
      onPressedChange={(next) => {
        if (next) setPopKey((current) => current + 1);
        onPressedChange(next);
      }}
      className="shrink-0 active:scale-[0.97] motion-reduce:active:scale-100 data-[state=on]:border-warning-border data-[state=on]:bg-warning-subtle data-[state=on]:text-warning-subtle-foreground"
    >
      <Icon
        key={popKey}
        name="star"
        size="xs"
        color="inherit"
        className={cn(
          "transition-[fill] duration-150 ease-out motion-reduce:transition-none",
          isPressed ? "fill-current" : "fill-transparent",
          isPressed && popKey > 0 && "favorite-star-pop",
        )}
        aria-hidden
      />
      {t("personaOpportunities.hub.scripts.favoritesOnly", "Favorites")}
    </Toggle>
  );
}

const SEARCH_FIELD: Record<OpportunityHubTabKey, keyof OpportunityHubFilters> = {
  sets: "setsSearch",
  opportunities: "opportunitiesSearch",
  scripts: "scriptsSearch",
  sent_to_vendor: "sentSearch",
};

function HubTabToolbar({
  tab,
  view,
  onViewChange,
  mix,
  opportunityPresence,
  postFlightOnlyView = false,
  legacyFacetOptions,
  filters,
  onFilterChange,
  onDatasetChange,
  onClearFilters,
  setOptions,
  tableControls,
}: {
  tab: OpportunityHubTabKey;
  view: OpportunityHubViewMode;
  onViewChange: (view: OpportunityHubViewMode) => void;
  mix: OpportunityHubSelectionMix;
  opportunityPresence: OpportunityFilterPresence;
  postFlightOnlyView?: boolean;
  legacyFacetOptions: {
    actions: readonly string[];
    datasets: readonly string[];
    features: readonly string[];
    subFeatures: readonly string[];
    engines: readonly string[];
  };
  filters: OpportunityHubFilters;
  onFilterChange: <K extends keyof OpportunityHubFilters>(
    key: K,
    value: OpportunityHubFilters[K],
  ) => void;
  onDatasetChange: (value: string) => void;
  onClearFilters: () => void;
  /** Selected sets. The set filter renders when there are two or more. */
  setOptions: readonly { id: string; name: string }[];
  /** Table-only controls (fold, columns, export) rendered beside the view toggle. */
  tableControls?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);

  React.useEffect(() => {
    setIsFiltersOpen(false);
  }, [tab]);

  const activeFilterCount = countActiveHubFilters(tab, filters, mix, opportunityPresence);
  const panelId = `hub-filter-panel-${tab}`;
  const searchField = SEARCH_FIELD[tab];
  const allTypesLabel = t("personaOpportunities.hub.sets.allTypes", "All types");
  const typeOptions: HubFilterOption[] = [
    { value: "all", label: allTypesLabel },
    { value: "PERSONA", label: hubSetTypeLabel(t, "PERSONA") },
    { value: "COMPETITOR", label: hubSetTypeLabel(t, "COMPETITOR") },
    { value: "USER_GENERATED", label: hubSetTypeLabel(t, "USER_GENERATED") },
    { value: "POST_FLIGHT", label: hubSetTypeLabel(t, "POST_FLIGHT") },
  ];

  const searchPlaceholder =
    tab === "sets"
      ? t("personaOpportunities.hub.sets.searchPlaceholder", "Search sets")
      : tab === "scripts"
        ? t("personaOpportunities.hub.scripts.searchPlaceholder", "Search scripts and briefs")
        : tab === "sent_to_vendor"
          ? t("personaOpportunities.hub.sent.searchPlaceholder", "Search orders")
          : mix.hasPersona && mix.hasCompetitor
            ? t("personaOpportunities.hub.opportunities.searchMixed", "Search opportunities")
            : mix.hasCompetitor
              ? t("personaOpportunities.hub.opportunities.searchCompetitor", "Search topics")
              : t("personaOpportunities.hub.opportunities.searchPersona", "Search angles");

  return (
    <div className="flex shrink-0 flex-col gap-3 rounded-md border border-border bg-card px-4 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Input
            value={filters[searchField]}
            onChange={(event) => onFilterChange(searchField, event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-8 w-full min-w-[10rem] sm:w-[16rem] sm:flex-none"
          />

          <FiltersToggleButton
            isOpen={isFiltersOpen}
            activeCount={activeFilterCount}
            onToggle={() => setIsFiltersOpen((open) => !open)}
            panelId={panelId}
          />
          {activeFilterCount > 0 ? <ClearFiltersButton onClear={onClearFilters} /> : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <OpportunityHubViewToggle view={view} onViewChange={onViewChange} />
          {tableControls}
        </div>
      </div>

      {isFiltersOpen ? (
        <div
          id={panelId}
          className="flex flex-wrap items-center gap-2 border-t border-border pt-3 duration-150 animate-in fade-in slide-in-from-top-1"
        >
          {tab === "sets" ? (
            <>
              <HubFilterSelect
                value={filters.setsStatus}
                onValueChange={(value) =>
                  onFilterChange("setsStatus", value as OpportunityHubSetStatusFilter)
                }
                ariaLabel={t("personaOpportunities.hub.sets.allStatuses", "All statuses")}
                options={[
                  {
                    value: "all",
                    label: t("personaOpportunities.hub.sets.allStatuses", "All statuses"),
                  },
                  {
                    value: "READY",
                    label: t("personaOpportunities.hub.sets.statusReady", "Ready"),
                  },
                  {
                    value: "GENERATING",
                    label: t("personaOpportunities.hub.sets.statusGenerating", "Generating"),
                  },
                  {
                    value: "FAILED",
                    label: t("personaOpportunities.hub.sets.statusFailed", "Failed"),
                  },
                ]}
              />
              <HubFilterSelect
                value={filters.setsType}
                onValueChange={(value) =>
                  onFilterChange("setsType", value as OpportunityHubSetTypeFilter)
                }
                ariaLabel={allTypesLabel}
                options={typeOptions}
              />
            </>
          ) : null}

          {tab === "opportunities" ? (
            <>
              {setOptions.length > 1 ? (
                <HubFilterSelect
                  value={filters.opportunitySet}
                  onValueChange={(value) => onFilterChange("opportunitySet", value)}
                  ariaLabel={t("personaOpportunities.hub.opportunities.allSets", "All sets")}
                  options={[
                    {
                      value: "all",
                      label: t("personaOpportunities.hub.opportunities.allSets", "All sets"),
                    },
                    ...setOptions.map((set) => ({ value: set.id, label: set.name })),
                  ]}
                />
              ) : null}
              {opportunityPresence.hasPersonaGroups || postFlightOnlyView ? (
                <HubFilterSelect
                  value={filters.opportunityStatus}
                  onValueChange={(value) =>
                    onFilterChange("opportunityStatus", value as PersonaOpportunityStatusFilter)
                  }
                  ariaLabel={t("personaOpportunities.hub.opportunities.allStatuses", "All statuses")}
                  options={[
                    {
                      value: "all",
                      label: t("personaOpportunities.hub.opportunities.allStatuses", "All statuses"),
                    },
                    { value: "NEW", label: t("personaOpportunities.active.statusNew", "New") },
                    {
                      value: "SCRIPTS_GENERATED",
                      label: t(
                        "personaOpportunities.active.statusScriptsGenerated",
                        "Scripts generated",
                      ),
                    },
                    {
                      value: "BRIEFS_GENERATED",
                      label: t(
                        "personaOpportunities.active.statusBriefsGenerated",
                        "Briefs generated",
                      ),
                    },
                    {
                      value: "SCRIPTS_AND_BRIEFS_GENERATED",
                      label: t(
                        "personaOpportunities.active.statusScriptsAndBriefsGenerated",
                        "Scripts + briefs generated",
                      ),
                    },
                    {
                      value: "SENT_TO_VENDOR",
                      label: t(
                        "personaOpportunities.active.statusSentToVendor",
                        "Sent to vendor",
                      ),
                    },
                  ]}
                />
              ) : null}
              {opportunityPresence.hasNoPersonaGroup && !postFlightOnlyView ? (
                <>
                  <HubFilterSelect
                    value={filters.opportunityAction}
                    onValueChange={(value) => onFilterChange("opportunityAction", value)}
                    ariaLabel={t("opportunities.filters.actionTooltip", "Filter by action")}
                    options={facetOptions(
                      legacyFacetOptions.actions,
                      filters.opportunityAction,
                      t("personaOpportunities.hub.opportunities.allActions", "All actions"),
                      (value) => value.charAt(0).toUpperCase() + value.slice(1),
                    )}
                  />
                  <HubFilterSelect
                    value={filters.opportunityFlow}
                    onValueChange={onDatasetChange}
                    ariaLabel={t("opportunities.filters.flowNameTooltip", "Filter by dataset")}
                    options={facetOptions(
                      legacyFacetOptions.datasets,
                      filters.opportunityFlow,
                      t("personaOpportunities.hub.opportunities.allDatasets", "All datasets"),
                      (value) => value,
                    )}
                  />
                  <HubFilterSelect
                    value={filters.opportunityFeature}
                    onValueChange={(value) => onFilterChange("opportunityFeature", value)}
                    ariaLabel={t("opportunities.filters.featureTooltip", "Filter by feature")}
                    options={facetOptions(
                      legacyFacetOptions.features,
                      filters.opportunityFeature,
                      t("personaOpportunities.hub.opportunities.allFeatures", "All features"),
                      (value) => value,
                    )}
                  />
                  <HubFilterSelect
                    value={filters.opportunitySubFeature}
                    onValueChange={(value) => onFilterChange("opportunitySubFeature", value)}
                    ariaLabel={t("opportunities.filters.subFeatureTooltip", "Filter by sub-feature")}
                    options={facetOptions(
                      legacyFacetOptions.subFeatures,
                      filters.opportunitySubFeature,
                      t("personaOpportunities.hub.opportunities.allSubFeatures", "All sub-features"),
                      (value) => value,
                    )}
                  />
                  <HubFilterSelect
                    value={filters.opportunityEngine}
                    onValueChange={(value) => onFilterChange("opportunityEngine", value)}
                    ariaLabel={t("opportunities.filters.engineTooltip", "Filter by engine")}
                    options={facetOptions(
                      legacyFacetOptions.engines,
                      filters.opportunityEngine,
                      t("personaOpportunities.hub.opportunities.allEngines", "All engines"),
                      (value) => formatOpportunityEngineLabel(value, t),
                    )}
                  />
                </>
              ) : null}
            </>
          ) : null}

          {tab === "scripts" ? (
            <>
              {setOptions.length > 1 ? (
                <HubFilterSelect
                  value={filters.scriptSet}
                  onValueChange={(value) => onFilterChange("scriptSet", value)}
                  ariaLabel={t("personaOpportunities.hub.opportunities.allSets", "All sets")}
                  options={[
                    {
                      value: "all",
                      label: t("personaOpportunities.hub.opportunities.allSets", "All sets"),
                    },
                    ...setOptions.map((set) => ({ value: set.id, label: set.name })),
                  ]}
                />
              ) : null}
              <HubFilterSelect
                value={filters.scriptKind}
                onValueChange={(value) =>
                  onFilterChange("scriptKind", value as OpportunityHubFilters["scriptKind"])
                }
                ariaLabel={t("personaOpportunities.hub.scripts.allKinds", "All kinds")}
                options={[
                  {
                    value: "all",
                    label: t("personaOpportunities.hub.scripts.allKinds", "All kinds"),
                  },
                  {
                    value: "script",
                    label: t("personaOpportunities.hub.scripts.kindScript", "Script"),
                  },
                  {
                    value: "brief",
                    label: t("personaOpportunities.hub.scripts.kindBrief", "Brief"),
                  },
                ]}
              />
              <HubFilterSelect
                value={filters.scriptPlatform}
                onValueChange={(value) =>
                  onFilterChange(
                    "scriptPlatform",
                    value as OpportunityHubFilters["scriptPlatform"],
                  )
                }
                ariaLabel={t("personaOpportunities.hub.scripts.allPlatforms", "All platforms")}
                options={[
                  {
                    value: "all",
                    label: t("personaOpportunities.hub.scripts.allPlatforms", "All platforms"),
                  },
                  {
                    value: "meta",
                    label: t("personaOpportunities.scripts.platform.meta", "Meta"),
                  },
                  {
                    value: "shorts",
                    label: t("personaOpportunities.scripts.platform.shorts", "YT Shorts"),
                  },
                  {
                    value: "in_stream",
                    label: t("personaOpportunities.scripts.platform.in_stream", "YT In-Stream"),
                  },
                ]}
              />
              <HubFilterSelect
                value={filters.scriptSent}
                onValueChange={(value) =>
                  onFilterChange("scriptSent", value as OpportunityHubFilters["scriptSent"])
                }
                ariaLabel={t(
                  "personaOpportunities.hub.scripts.allSendStatuses",
                  "All send statuses",
                )}
                options={[
                  {
                    value: "all",
                    label: t(
                      "personaOpportunities.hub.scripts.allSendStatuses",
                      "All send statuses",
                    ),
                  },
                  {
                    value: "sent",
                    label: t(
                      "personaOpportunities.scripts.toolbar.sendStatusSent",
                      "Sent to vendor",
                    ),
                  },
                  {
                    value: "not_sent",
                    label: t(
                      "personaOpportunities.scripts.toolbar.sendStatusNotSent",
                      "Not sent",
                    ),
                  },
                ]}
              />
              <HubFavoritesToggle
                isPressed={filters.scriptFavorites === "favorites"}
                onPressedChange={(isPressed) =>
                  onFilterChange("scriptFavorites", isPressed ? "favorites" : "all")
                }
              />
            </>
          ) : null}

        </div>
      ) : null}
    </div>
  );
}

const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Matches the set-card grid: 1 / 2 / 3 / 4 columns. */
function useSetGridColumns(): number {
  const [columns, setColumns] = React.useState(1);
  React.useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      if (width >= 1536) setColumns(4);
      else if (width >= 1024) setColumns(3);
      else if (width >= 640) setColumns(2);
      else setColumns(1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return columns;
}
